import React, { useRef, useState } from 'react'
import { ArrowLeftIcon, GiftIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import Panel from '../../../../../components/Panel'
import Badge from '../../../../../components/Badge'
import Button from '../../../../../components/Form/Button'
import DisplayField from '../../../../../components/Form/DisplayField'
import Input from '../../../../../components/Form/Input'
import CancellationPanel from './CancellationPanel'
import ConfirmDialogShell from './ConfirmDialogShell'
import { IncentivePlan } from '../../../../../apis/types'
import { cancelAccommodationCreditApi, reverseAccommodationCreditApi } from '../../../../../apis/apis'
import { pushToast } from '../../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../../shared/redux/hooks'
import { formatUTCDetailedDate, getApiErrorMessage } from '../../../../../util/string'
import { useIncentivePlanAuditHistory } from './useIncentivePlanAuditHistory'
import IncentiveActivityLogPanel from './IncentiveActivityLogPanel'
import { formatDateByText, getCreatedDetails } from './incentiveDisplay'

const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0)
}

const APPLIED_STATUSES = ['CREDITED', 'BILLED', 'COMPLETED']

interface CreditDetailProps {
    credit: IncentivePlan
    creditAccountType: 'King' | 'Utility'
    hasCancelPermission: boolean
    hasReversePermission: boolean
    onBack: () => void
    onReversed: () => void | Promise<void>
    tenantAccountId: string
}

const CreditDetail: React.FC<CreditDetailProps> = ({
    credit,
    creditAccountType,
    hasCancelPermission,
    hasReversePermission,
    onBack,
    onReversed,
    tenantAccountId,
}) => {
    const dispatch = useAppDispatch()
    const [showCancelForm, setShowCancelForm] = useState(false)
    const [cancelSubmitting, setCancelSubmitting] = useState(false)
    const [showReversalForm, setShowReversalForm] = useState(false)
    const { auditHistory, isLoading: auditLoading } = useIncentivePlanAuditHistory(tenantAccountId, credit.id)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [reversalReason, setReversalReason] = useState('')
    const [reversalSubmitting, setReversalSubmitting] = useState(false)
    const [showReversalConfirm, setShowReversalConfirm] = useState(false)
    const reversalLockedRef = useRef(false)

    const isReversed = credit.status === 'REVERSED'

    const hasPendingInstallments = credit.installments.some(
        (i) => ['PENDING', 'SCHEDULED'].includes(i.status)
    )
    const displayStatus =
        credit.status === 'CREDITED' && hasPendingInstallments ? 'PARTIALLY_CREDITED' : credit.status

    const startingBalance = parseFloat(credit.startingBalance as string)
    const amountCredited = credit.installments
        .filter((i) => APPLIED_STATUSES.includes(i.status))
        .reduce((sum, i) => sum + Number(i.amount), 0)
    const remainingCredit = credit.installments
        .filter((i) => ['PENDING', 'SCHEDULED'].includes(i.status))
        .reduce((sum, i) => sum + Number(i.amount), 0)

    const appliedInstallments = credit.installments.filter((i) => APPLIED_STATUSES.includes(i.status))
    const hasApplied = appliedInstallments.length > 0

    const pendingInstallments = credit.installments.filter(
        (i) => !APPLIED_STATUSES.includes(i.status) && i.status !== 'CANCELLED'
    )

    const creditedInstallments = credit.installments.filter((i) => i.status === 'CREDITED').length
    const progressPercent =
        credit.installmentCount > 0
            ? Math.round((creditedInstallments / credit.installmentCount) * 100)
            : 0

    const createdDetails = getCreatedDetails(credit.createdAt, credit.createdBy, credit.memo)
    const reversedByDateText = formatDateByText((credit as any).reversedAt, (credit as any).reversedBy)

    const handleCancelSubmit = async (reason: string) => {
        setCancelSubmitting(true)
        try {
            await cancelAccommodationCreditApi(tenantAccountId, credit.id, reason)
            await onReversed()
            setShowCancelForm(false)
            dispatch(pushToast({ message: 'Accommodation credit cancelled successfully', type: 'success' }))
        } catch (e: any) {
            dispatch(
                pushToast({
                    message: 'Failed to cancel accommodation credit',
                    type: 'error',
                    description: getApiErrorMessage(e),
                })
            )
        } finally {
            setCancelSubmitting(false)
        }
    }

    const handleReversalSubmit = async () => {
        if (reversalLockedRef.current || reversalSubmitting) return
        reversalLockedRef.current = true
        setReversalSubmitting(true)
        try {
            await reverseAccommodationCreditApi(tenantAccountId, credit.id, reversalReason.trim(), selectedIds)
            await onReversed()
            setShowReversalConfirm(false)
            setShowReversalForm(false)
            dispatch(pushToast({ message: 'Accommodation credit reversed successfully', type: 'success' }))
        } catch (e: any) {
            dispatch(
                pushToast({
                    message: 'Failed to reverse accommodation credit',
                    type: 'error',
                    description: getApiErrorMessage(e),
                })
            )
        } finally {
            reversalLockedRef.current = false
            setReversalSubmitting(false)
        }
    }

    const toggleInstallment = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        )
    }

    const cadenceLabel =
        credit.cadence === 'ONE_TIME'
            ? 'One-time'
            : credit.cadence === 'MONTHLY'
                ? `${credit.installmentCount} months`
                : credit.cadence?.replaceAll('_', ' ')

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Incentives
                </button>
                <div className="flex items-center gap-2">
                    {
                        (credit.status === 'ACTIVE' || (credit.status === 'CREDITED' && hasPendingInstallments)) &&
                        !showCancelForm &&
                        !showReversalForm && (
                            hasCancelPermission
                                ?
                                <Button size={2} color="red" onClick={() => setShowCancelForm(true)}>
                                    Cancel Credit
                                </Button> 
                                :
                                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                    <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                    Sufficient permission required to cancel an accommodation credit.
                                </span>
                        )
                    }
                    {
                        ['CANCELLED', 'CREDITED', 'COMPLETED'].includes(credit.status) &&
                        hasApplied &&
                        !showReversalForm &&
                        !showCancelForm && (
                            hasReversePermission
                                ?
                                <Button size={2} color="red" onClick={() => setShowReversalForm(true)}>
                                    Reverse Installments
                                </Button>
                                :
                                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                    <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                    Sufficient permission required to reverse an accommodation credit.
                                </span>
                        )
                    }
                </div>
            </div>

            {/* Title row */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-emerald-100 dark:bg-emerald-900/40 shrink-0">
                    <GiftIcon className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {creditAccountType} Accommodation Credit
                </h3>
                {(credit.humanId ?? credit.id) && (
                    <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                        {credit.humanId ?? credit.id}
                    </span>
                )}
                <Badge status={displayStatus} />
            </div>

            {/* Cancel flow — no applied installments */}
            {showCancelForm && (
                <CancellationPanel
                    title="Cancel accommodation credit"
                    warningMessage="Cancelling this credit plan will void all of the following pending and scheduled credit installments. This action cannot be undone."
                    extraContent={
                        pendingInstallments.length > 0 ? (
                            <div className="border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-200 dark:divide-slate-700">
                                {pendingInstallments.map((installment) => (
                                    <div key={installment.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                                        <span className="flex-1 text-gray-900 dark:text-gray-100">
                                            Installment {installment.number} — {formatUTCDetailedDate(new Date(installment.dueDate))}
                                        </span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                            {formatCurrency(installment.amount)}
                                        </span>
                                        <Badge status={installment.status} />
                                    </div>
                                ))}
                            </div>
                        ) : undefined
                    }
                    reasonLabel="Cancellation reason"
                    reasonPlaceholder="Describe the reason for cancelling this credit..."
                    confirmActionLabel="Confirm cancellation"
                    dismissActionLabel="Keep credit"
                    confirmModalTitle="Cancel this accommodation credit?"
                    summaryRows={[
                        {
                            label: 'Credit',
                            value: credit.humanId ?? credit.id,
                            valueClassName: 'font-mono font-medium dark:text-gray-200',
                        },
                        {
                            label: 'Installments to void',
                            value: `${pendingInstallments.length} of ${credit.installmentCount}`,
                            valueClassName: 'font-medium text-red-600 dark:text-red-400',
                        },
                        {
                            label: 'Remaining credit',
                            value: formatCurrency(remainingCredit),
                            valueClassName: 'font-medium text-red-600 dark:text-red-400',
                        },
                    ]}
                    isSubmitting={cancelSubmitting}
                    onConfirm={handleCancelSubmit}
                    onDismiss={() => setShowCancelForm(false)}
                />
            )}

            {/* Reverse flow — has applied installments */}
            {showReversalForm && (
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <div className="py-2 px-4 font-semibold rounded-t-lg bg-indigo-100 dark:bg-slate-700">
                        Reverse accommodation credit
                        <p className="text-sm font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                            Select credit installments to reverse.
                        </p>
                    </div>
                    <div className="p-4 space-y-4 bg-white dark:bg-slate-800">
                        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                            <InformationCircleIcon className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700 dark:text-red-300">
                                Selected credit installments already applied to invoices will be reversed (&ldquo;clawed back&rdquo;) on the next invoice.
                            </p>
                        </div>

                        {/* Installment checkbox list */}
                        <div className="border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-200 dark:divide-slate-700">
                            {credit.installments.map((installment) => {
                                const selectable = APPLIED_STATUSES.includes(installment.status)
                                const checked = selectedIds.includes(installment.id)
                                return (
                                    <label
                                        key={installment.id}
                                        className={`flex items-center gap-3 px-4 py-3 text-sm ${selectable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50' : 'opacity-50 cursor-not-allowed'}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={!selectable}
                                            onChange={() => selectable && toggleInstallment(installment.id)}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="flex-1 text-gray-900 dark:text-gray-100">
                                            Installment {installment.number} — {formatUTCDetailedDate(new Date(installment.dueDate))}
                                        </span>
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                            {formatCurrency(installment.amount)}
                                        </span>
                                        <Badge status={installment.status} />
                                    </label>
                                )
                            })}
                        </div>

                        <Input
                            label="Reversal reason"
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            placeholder="Describe the reason for reversing these installments..."
                            required
                        />

                        <div className="flex gap-2">
                            <Button
                                color="red"
                                disabled={selectedIds.length === 0 || !reversalReason.trim() || reversalSubmitting}
                                onClick={() => setShowReversalConfirm(true)}
                            >
                                Confirm reversal
                            </Button>
                            <Button
                                color="slate"
                                disabled={reversalSubmitting}
                                onClick={() => {
                                    setShowReversalForm(false)
                                    setSelectedIds([])
                                    setReversalReason('')
                                }}
                            >
                                Keep credit
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialogShell isOpen={showReversalConfirm}>
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 shrink-0">
                        <InformationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Reverse these installments?</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            This action is permanent and cannot be undone.
                        </p>
                    </div>
                </div>
                <div className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
                    <div className="flex justify-between px-4 py-2">
                        <span className="text-gray-500 dark:text-gray-400">Credit</span>
                        <span className="font-mono font-medium dark:text-gray-200">{credit.humanId ?? credit.id}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2">
                        <span className="text-gray-500 dark:text-gray-400">Installments selected</span>
                        <span className="font-medium dark:text-gray-200">{selectedIds.length} of {credit.installments.length}</span>
                    </div>
                    <div className="flex justify-between items-start px-4 py-2 gap-4">
                        <span className="text-gray-500 dark:text-gray-400 shrink-0">Reason</span>
                        <span className="font-medium dark:text-gray-200 text-right">{reversalReason}</span>
                    </div>
                </div>
                <div className="flex gap-2 justify-end">
                    <Button
                        color="slate"
                        onClick={() => setShowReversalConfirm(false)}
                        disabled={reversalSubmitting}
                    >
                        Go back
                    </Button>
                    <Button
                        color="red"
                        isSaving={reversalSubmitting}
                        disabled={reversalSubmitting}
                        onClick={handleReversalSubmit}
                    >
                        {reversalSubmitting ? 'Reversing...' : 'Confirm reversal'}
                    </Button>
                </div>
            </ConfirmDialogShell>

            {/* Reversal info */}
            {isReversed && (credit as any).reversalReason && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                    <InformationCircleIcon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Credit reversed</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {(credit as any).reversalReason}
                        </p>
                        {reversedByDateText && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                Reversed {reversedByDateText}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Credit Details */}
                <Panel header="Credit Details">
                    <div className="space-y-3 pt-1">
                        <DisplayField label="Cadence">{cadenceLabel}</DisplayField>
                        <DisplayField label="Start Date">
                            {formatUTCDetailedDate(new Date(credit.startDate))}
                        </DisplayField>
                        {credit.endDate && (
                            <DisplayField label="End Date">
                                {formatUTCDetailedDate(new Date(credit.endDate))}
                            </DisplayField>
                        )}
                        <DisplayField label="Created">
                            <>
                                {createdDetails.dateByText && <span className="block">{createdDetails.dateByText}</span>}
                                {createdDetails.reason && <span className="block text-xs text-gray-500 dark:text-gray-400">{createdDetails.reason}</span>}
                            </>
                        </DisplayField>
                    </div>
                </Panel>

                {/* Financial Summary */}
                <Panel header="Financial Summary">
                    <div className="space-y-4 pt-1">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Total Credit Amount</p>
                            <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
                                {formatCurrency(startingBalance)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Amount Applied</p>
                            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(amountCredited)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Remaining Credit</p>
                            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(remainingCredit)}
                            </p>
                        </div>
                    </div>
                </Panel>

                {/* Progress */}
                <Panel header="Progress">
                    <div className="space-y-4 pt-1">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Credits applied</p>
                            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                                {creditedInstallments} of {credit.installmentCount}
                            </p>
                        </div>
                        <div className="pt-1">
                            <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500 dark:bg-green-400 transition-all"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{progressPercent}% applied</p>
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Installment Schedule */}
            <Panel header="Installment Schedule">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead className="bg-white dark:bg-slate-950">
                            <tr>
                                {['Installment', 'Credit Date', 'Amount Credited', 'Remaining Balance', 'Status', 'Applied Date'].map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider dark:text-white"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {credit.installments.map((installment) => (
                                <tr key={installment.id} className="bg-slate-50 dark:bg-slate-950/50">
                                    <td className="px-4 py-3 text-sm font-medium dark:text-gray-200">
                                        Installment {installment.number}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatUTCDetailedDate(new Date(installment.dueDate))}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 font-medium">
                                        {formatCurrency(installment.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatCurrency(installment.remainingBalance)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge status={installment.status} />
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        {(installment as any).paidDate
                                            ? formatUTCDetailedDate(new Date((installment as any).paidDate))
                                            : '\u2014'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <IncentiveActivityLogPanel auditHistory={auditHistory} isLoading={auditLoading} />
        </div>
    )
}

export default CreditDetail
